// upload-portfolio-refactored v1: 重構後的作品集上傳組件

import React, { useState } from 'react';

import { SuccessModal, ErrorModal } from '../ui';

import {
  FileUploadSection,
  FormFields,
  CustomSoftwareModal,
  ConfirmUploadModal,
  SubmitButton,
} from './components';
import { usePortfolioForm, usePortfolioActions } from './hooks';
import { isValidVideoUrl } from './utils/formDataHelpers';

/**
 * 重構後的作品集上傳組件
 * @returns {JSX.Element} 作品集上傳組件
 */
const UploadPortfolio_refactored = () => {
  // 上传模式状态：'images' 或 'video'
  const [uploadMode, setUploadMode] = useState('images');
  const [savedVideoUrl, setSavedVideoUrl] = useState('');

  // 使用自定義 Hooks
  const {
    formData,
    tagInput,
    errors,
    handleFormChange,
    handleAddCustomSoftware,
    handleRemoveSoftware,
    handleTagInputChange,
    handleTagInputKeyDown,
    handleRemoveTag,
    validateForm,
    setErrors,
    tagSuggestions,
    isFetchingTagSuggestions,
    handleSelectSuggestedTag,
  } = usePortfolioForm();

  const {
    selectedFiles,
    isSubmitting,
    uploadProgress,
    showSuccessModal,
    showErrorModal,
    errorMessage,
    showConfirmModal,
    handleFileUpload,
    handleRemoveFile,
    handleSubmit,
    handleConfirmUpload,
    handleCloseSuccessModal,
    handleCloseErrorModal,
    handleCloseConfirmModal,
  } = usePortfolioActions();

  // 自定義軟件模態框狀態
  const [showCustomSoftwareModal, setShowCustomSoftwareModal] = useState(false);

  // 處理顯示自定義軟件模態框
  const handleShowCustomSoftwareModal = () => {
    setShowCustomSoftwareModal(true);
  };

  // 處理關閉自定義軟件模態框
  const handleCloseCustomSoftwareModal = () => {
    setShowCustomSoftwareModal(false);
  };

  // 處理添加自定義軟件
  const handleAddCustomSoftwareWithClose = customSoftware => {
    const success = handleAddCustomSoftware(customSoftware);
    if (success) {
      handleCloseCustomSoftwareModal();
    }
  };

  // 處理表單提交
  const handleFormSubmit = e => {
    handleSubmit(e, formData, validateForm);
  };

  // 處理確認上傳
  const handleConfirmUploadWithData = () => {
    handleConfirmUpload(formData, savedVideoUrl);
  };

  // 处理保存视频链接
  const handleSaveVideoUrl = () => {
    if (isValidVideoUrl(formData.videoUrl)) {
      setSavedVideoUrl(formData.videoUrl);
      setUploadMode('images'); // 切换回 HQ Images 模式
      // 清空 videoUrl 输入框
      handleFormChange({ target: { name: 'videoUrl', value: '' } });
    }
  };

  // 处理移除保存的视频链接
  const handleRemoveVideoUrl = () => {
    setSavedVideoUrl('');
  };

  // 处理切换到 Video 模式
  const handleSwitchToVideo = () => {
    setUploadMode('video');
    // 将已保存的视频链接填充到输入框中
    if (savedVideoUrl) {
      handleFormChange({ target: { name: 'videoUrl', value: savedVideoUrl } });
    }
  };

  return (
    <div className='bg-white p-6'>
      {/* 图标选项区域 */}
      <div className='flex items-center justify-center mb-6'>
        {/* HQ Images 选项 */}
        <button
          onClick={() => setUploadMode('images')}
          className={`flex flex-col items-center px-6 py-3 transition-all duration-200 ${
            uploadMode === 'images'
              ? 'bg-blue-50 border-2 border-tag-blue'
              : 'hover:bg-gray-50'
          }`}
        >
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${
              uploadMode === 'images' ? 'bg-tag-blue' : 'bg-blue-100'
            }`}
          >
            <svg
              className={`w-5 h-5 ${
                uploadMode === 'images' ? 'text-white' : 'text-tag-blue'
              }`}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
              />
            </svg>
          </div>
          <h3
            className={`text-sm font-medium mb-1 ${
              uploadMode === 'images' ? 'text-tag-blue' : 'text-gray-800'
            }`}
            style={{
              fontFamily: 'Inter, sans-serif',
              letterSpacing: '-0.025em',
            }}
          >
            HQ Images
          </h3>
          <p className='text-xs text-gray-500'>JPG, PNG, GIF</p>
        </button>

        {/* 竖线分隔符 */}
        <div className='w-px h-16 bg-gray-200 mx-4'></div>

        {/* Video 选项 */}
        <button
          onClick={handleSwitchToVideo}
          className={`flex flex-col items-center px-6 py-3 transition-all duration-200 ${
            uploadMode === 'video'
              ? 'bg-blue-50 border-2 border-tag-blue'
              : 'hover:bg-gray-50'
          }`}
        >
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${
              uploadMode === 'video' ? 'bg-tag-blue' : 'bg-blue-100'
            }`}
          >
            <svg
              className={`w-5 h-5 ${
                uploadMode === 'video' ? 'text-white' : 'text-tag-blue'
              }`}
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z'
              />
            </svg>
          </div>
          <h3
            className={`text-sm font-medium mb-1 ${
              uploadMode === 'video' ? 'text-tag-blue' : 'text-gray-800'
            }`}
            style={{
              fontFamily: 'Inter, sans-serif',
              letterSpacing: '-0.025em',
            }}
          >
            Video
          </h3>
          <p className='text-xs text-gray-500'>YouTube, Vimeo</p>
        </button>
      </div>

      {/* 錯誤顯示 */}
      {errors.general && (
        <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md'>
          <p className='text-red-600 text-sm'>{errors.general}</p>
        </div>
      )}

      {/* 文件上傳區域 */}
      {uploadMode === 'images' ? (
        <FileUploadSection
          selectedFiles={selectedFiles}
          onFileUpload={handleFileUpload}
          onRemoveFile={handleRemoveFile}
          isSubmitting={isSubmitting}
          uploadProgress={uploadProgress}
          errors={errors}
          savedVideoUrl={savedVideoUrl}
          onRemoveVideoUrl={handleRemoveVideoUrl}
        />
      ) : (
        /* Video URL 输入区域 */
        <div className='mb-6'>
          <p className='text-sm text-gray-600 mb-4'>
            Paste a Vimeo or YouTube video URL here
          </p>
          <div className='flex gap-3'>
            <input
              type='url'
              name='videoUrl'
              value={formData.videoUrl || ''}
              onChange={handleFormChange}
              placeholder='Video URL'
              className='flex-1 px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-tag-blue focus:border-transparent'
            />
            <button
              type='button'
              onClick={handleSaveVideoUrl}
              disabled={!isValidVideoUrl(formData.videoUrl)}
              className={`px-6 py-3 transition-colors duration-200 ${
                isValidVideoUrl(formData.videoUrl)
                  ? 'bg-tag-blue text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                letterSpacing: '-0.025em',
              }}
            >
              Save
            </button>
          </div>
          {errors.videoUrl && (
            <p className='text-red-600 text-sm mt-2'>{errors.videoUrl}</p>
          )}
        </div>
      )}

      {/* 表單字段 */}
      <form onSubmit={handleFormSubmit} className='space-y-4'>
        <FormFields
          formData={formData}
          tagInput={tagInput}
          onFormChange={handleFormChange}
          onTagInputChange={handleTagInputChange}
          onTagInputKeyDown={handleTagInputKeyDown}
          onRemoveSoftware={handleRemoveSoftware}
          onRemoveTag={handleRemoveTag}
          onShowCustomSoftwareModal={handleShowCustomSoftwareModal}
          isSubmitting={isSubmitting}
          errors={errors}
          tagSuggestions={tagSuggestions}
          onSelectSuggestedTag={handleSelectSuggestedTag}
          isFetchingTagSuggestions={isFetchingTagSuggestions}
        />

        {/* 提交按鈕 */}
        <SubmitButton isSubmitting={isSubmitting} onSubmit={handleFormSubmit} />
      </form>

      {/* 自定義軟件輸入模態框 */}
      <CustomSoftwareModal
        isOpen={showCustomSoftwareModal}
        onClose={handleCloseCustomSoftwareModal}
        onAdd={handleAddCustomSoftwareWithClose}
      />

      {/* 確認上傳彈窗 */}
      <ConfirmUploadModal
        isOpen={showConfirmModal}
        onClose={handleCloseConfirmModal}
        onConfirm={handleConfirmUploadWithData}
      />

      {/* 成功提示模態框 */}
      <SuccessModal
        isOpen={showSuccessModal}
        title='🎉 Success!'
        message='Portfolio work published successfully to TAG!'
        onClose={handleCloseSuccessModal}
      />

      {/* 錯誤提示模態框 */}
      <ErrorModal
        isOpen={showErrorModal}
        title='⚠️ Error'
        message={errorMessage}
        onClose={handleCloseErrorModal}
      />
    </div>
  );
};

export default UploadPortfolio_refactored;
